import React, { useEffect, useState, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';

import AppHeader from '../../components/AppHeader';
import AppSidebar from '../../components/AppSidebar';
import PageHeader from '../../components/PageHeader';
import RecentActivity from '../../components/RecentActivity';
import LogsTable from '../../components/LogsTable';

import ProductTicketService from '../../services/ProductTicketService';
import SubscriptionTicketService from '../../services/SubscriptionTicketService';
import ReplyService from '../../services/ReplyService';
import UsersService from '../../services/UsersService';
import CallLogService from '../../services/CallLogService';

import Assign from '../../modals/Assign';
import EditTicketModal from '../customers/components/modals/EditTicketModal';
import CallLog from '../../modals/CallLog';

import { useAuth } from '../../contexts/AuthContext';
import { statusClasses, priorityClasses, priorityOptions, statusOptions } from '../../utils/OptionUtils';
import { DateFormat , DateTimeFormat } from '../../utils/DateFormat';
import AnonymousTicketService from '../../services/AnonymousTicketService';

const ViewTicket = () => {
    const { register, handleSubmit, formState: { errors }, reset } = useForm();
    const referenceForm = useForm();
    const { id, service } = useParams();
    const [ticket, setTicket] = useState([]);
    const type = service === 'basic' ? 'product_ticket' : service === 'premium' ? 'subscription_ticket' : service === 'anonymous' ? 'anonymous_ticket' : '';
    const [comments, setComments] = useState([]);
    const { user } = useAuth();
    const [showStatusPriorityModal, setShowStatusPriorityModal] = useState(false);
    const [ticketId, setTicketId] = useState(null);
    const [columnType, setColumnType] = useState(null);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const statusPriorityForm = useForm();
    const assignForm = useForm();
    const callLogForm = useForm();
    const [usersList, setUsersList] = useState([]);
    const [showCallLogModal, setShowCallLogModal] = useState(false);
    const [statusData, setStatusData] = useState('');
    const [callLogData, setCallLogData] = useState({});
    const [selectedStatus, setSelectedStatus] = useState('');
    const today = new Date().toISOString().split('T')[0];
    const [allCallLogs, setAllCallLogs] = useState([]);
    const [loading, setLoading] = useState(true);


    useEffect(() => {
        const fetchTicket = async () => {
            try {
                let selectedService = null;
                if (service === 'basic') {
                    selectedService = ProductTicketService;
                } else if (service === 'premium') {
                    selectedService = SubscriptionTicketService;
                } else {
                    selectedService = AnonymousTicketService;
                }
                const response = await selectedService.get(id);

                if (response.status === 200) {
                    setTicket(response?.data?.data);
                    referenceForm.setValue('reference_ticket', response?.data?.data?.reference_ticket || '');
                } else {
                    console.error('Unexpected response:', response);
                }
            } catch (error) {
                toast.error(error?.response?.data?.message || error?.message || "An error occurred");
                console.error('Error fetching ticket:', error.message);
            }
        };

        if (id && service) {
            fetchTicket();
        }
    }, [id, service, referenceForm]);

    useEffect(() => {
        const getByTicketId = async () => {
            try {
                console.log('service', service);
                const ticketType = service === 'basic' ? 'product' : service === 'premium' ? 'premium' : service === 'anonymous' ? 'anonymous' : '';
                const response = await ReplyService.getByTicketId(id, ticketType);
                if (response.status === 200) {
                    setComments(response?.data?.data);
                } else {
                    console.error('Unexpected response:', response);
                }
            } catch (error) {
                toast.error(error?.response?.data?.message || error?.message || "An error occurred");
                console.error('Error fetching replies:', error.message);
            }
        };

        if (id && service) {
            getByTicketId();
        }
    }, [id, service]);

    const onSubmit = async (data) => {
        try {

            data.ticket_id = id;
            data.ticket_type = service === 'basic' ? 'product' : service === 'subscription' ? 'subscription' : service === 'anonymous' ? 'anonymous' : '';
            data.replier_type = user?.guard || 'user';
            data.replier_id = user.id;
            const response = await ReplyService.create({ data });

            if (response.status === 201) {
                toast.success(response?.data?.message);
                setComments([...comments, response?.data?.data]);
                reset();
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || error?.message || "An error occurred");
        }
    };
    const handleColumnChange = (record, type) => {
        setTicketId(record.id);
        setColumnType(type);
        setShowStatusPriorityModal(true);
        reset({ [type]: record[type] });
    };

    const handleAssign = async (id) => {
        setTicketId(id);
        try {
            const response = await UsersService.list();
            if (response.status === 200) {
                const data = response?.data?.data || [];
                const formattedUsers = data.map(user => ({
                    value: user.id,
                    label: user.name + '-' + user.email,
                }));
                setUsersList(formattedUsers);
                setShowAssignModal(true);
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || error?.message || "An error occurred");
        }
    };

    const onStatusPrioritySubmit = async (data) => {
        try {
            const selectedService = service === 'basic' ? ProductTicketService : service === 'subscription' ? SubscriptionTicketService : AnonymousTicketService;
            const response = await selectedService.changeStatusOrPriority(ticketId, { data }, columnType);
            if (response.status === 200) {
                toast.success(response?.data?.message);
                setTicket((prevTicket) => ({
                    ...prevTicket,
                    [columnType]: data[columnType],
                }));
                setShowStatusPriorityModal(false);
                reset();
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || error?.message || "An error occurred");
        }
    };

    const onAssignSubmit = async (data) => {
        try {
            const assignedTo = data.assignee[0]?.value;
            data.assigned_to = assignedTo;
            delete data?.assignee;
            const selectedService = service === 'basic' ? ProductTicketService : service === 'subscription' ? SubscriptionTicketService : AnonymousTicketService;
            const response = await selectedService.assign(ticketId, { data });
            if (response.status === 200) {
                const resData = response?.data?.data;
                toast.success(response?.data?.message);
                setTicket((prevTicket) => ({
                    ...prevTicket,
                    assigned_to: resData.assigned_to,
                    assignedTo: { name: resData.assignedTo?.name },
                }))
                setShowAssignModal(false);
                reset();
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || error?.message || "An error occurred");
        }
    };

    const handleClose = () => {
        setShowStatusPriorityModal(false);
        setShowAssignModal(false);
        setShowCallLogModal(false);
    };

    const fetchCallLogs = useCallback(async () => {
        try {
            setLoading(true);
            const response = await CallLogService.getLogByType(type, id);
            if (response.status === 200) {
                setAllCallLogs(response?.data?.data || []);
            } else {
                console.error('Error fetching data:', response);
            }
        } catch (error) {
            console.error(error?.response?.data?.message || error?.message || "An error occurred");
        } finally {
            setLoading(false);
        }
    }, [type, id]);

    useEffect(() => {
        fetchCallLogs();
    }, [fetchCallLogs]);

    const handleButtonClick = () => {
        console.log("Import button clicked");
        setShowCallLogModal(true);
    };
    const handleCallLog = (e) => {
        const value = e.target.value;
        setSelectedStatus(value);
        setCallLogData(prevState => ({ ...prevState, status: value }));
        if (value === 'connected' || value === 'disconnected') {
            setStatusData(
                <div>
                    <form onSubmit={handleSubmit(onSubmit)}>
                        <div className="form-group">
                            <label htmlFor="rescheduleDate" className="form-label">Reschedule Date & Time</label>
                            <input
                                type="date"
                                className="form-control mb-2"
                                id="rescheduleDate"
                                {...register('date', { required: 'Date is required' })}
                                onChange={(e) => setCallLogData({ ...callLogData, date: e.target.value })}
                            />
                            {errors.date && <p className="text-danger">{errors.date.message}</p>}
                        </div>
                        <div className="form-group mt-3">
                            <textarea
                                className="form-control"
                                id="callRemark"
                                rows="2"
                                placeholder="Call Remark"
                                {...register('remarks', { required: 'Remark is required' })}
                                onChange={(e) => setCallLogData({ ...callLogData, remarks: e.target.value })}
                            ></textarea>
                            {errors.remarks && <p className="text-danger">{errors.remarks.message}</p>}
                        </div>
                        {value === 'connected' && (
                            <div className="form-group mt-3">
                                <select
                                    className="form-select"
                                    {...register('feedback_type', { required: 'Feedback type is required' })}
                                    onChange={(e) => setCallLogData({ ...callLogData, feedback_type: e.target.value })}
                                >
                                    <option value="">Select Feedback Type</option>
                                    <option value="positive">Positive</option>
                                    <option value="negative">Negative</option>
                                </select>
                                {errors.feedback_type && <p className="text-danger">{errors.feedback_type.message}</p>}
                            </div>
                        )}
                        <div className="form-group mt-3">
                            <select
                                className="form-select"
                                {...register('response', { required: 'response type is required' })}
                                onChange={(e) => setCallLogData({ ...callLogData, response: e.target.value })}
                            >
                                <option value="">Select Response Type</option>
                                <option value="interested">Interested</option>
                                <option value="not_interested">Not Interested</option>
                            </select>
                            {errors.feedback_type && <p className="text-danger">{errors.feedback_type.message}</p>}
                        </div>
                    </form>
                </div>
            );
        } else {
            setStatusData(null);
        }
    };

    const onCallLogSubmit = async (data) => {

        try {
            const requestData = {
                type: service === 'basic' ? 'product_ticket' : service === 'subscription' ? 'subscription_ticket' : service === 'anonymous' ? 'anonymous_ticket' : "",
                type_id: parseInt(id) || 0,
                status: data?.status || '',
                feedback_type: data?.feedback_type || "negative",
                response: data?.response || "not_interested",
                remarks: data?.remarks || "NA",
                rescheduled_at: data.date || null,
            }

            const response = await CallLogService.createLog({ data: requestData });
            if (response.status === 201) {
                toast.success(response?.data?.message);
                setAllCallLogs(prevLogs => [...prevLogs, response.data.data]);
                setShowCallLogModal(false);
                reset();
            } else {
                toast.error(response?.data?.message);
            }
        } catch (error) {
            toast.error(error?.message || "An error occurred");
        }
    };

    if (loading) return <p>Loading...</p>;

    const onReferenceTicketSubmit = async (data) => {
        try {
            const reference_ticket = data?.reference_ticket || null;
            const response = await ProductTicketService.update(id, { data }, reference_ticket);
            if (response.status === 200) {
                toast.success(response?.data?.message);
                reset();
            } else {
                toast.error(response?.data?.message);
            }
        } catch (error) {
            toast.error(error?.message || "An error occurred");
        }
    };


    return (
        <>
            <AppSidebar />
            <div className="main-content">
                <AppHeader title="Tickets" />
                <div className="container-fluid">
                    <div className="row justify-content-center">
                        <div className="col-12">
                            <PageHeader
                                title="View Ticket"
                                button_label="Back"
                                link_to="/tickets"
                                className="btn btn-sm btn-primary me-2"
                                button_label1="Log a Call"
                                buttonAttributes1={{ onClick: handleButtonClick }}
                                className1="btn btn-sm btn-info"
                            />
                        </div>
                        <div className="tab-content">
                            <div
                                className="tab-pane fade show active"
                                id="contactsListPane"
                                role="tabpanel"
                                aria-labelledby="contactsListTab"
                            >
                                <div className="card">
                                    <div className="card-header">
                                        <div className="row align-items-center">
                                            <div className="col d-flex justify-content-between align-items-center">
                                                <h3>{ticket?.title || 'No Title Available'} - ({ticket?.ticket_no || 'No Ticket Number Available'})</h3>
                                                <div className="d-flex">
                                                    <div className="me-4">
                                                        <strong>Status:</strong>
                                                        <span
                                                            className={`editable-field badge ${statusClasses[ticket.status] || ''}`}
                                                            onClick={() => handleColumnChange(ticket, 'status')}
                                                        >
                                                            {ticket.status?.toUpperCase() || 'N/A'}
                                                        </span>
                                                    </div>
                                                    <div>
                                                        <strong>Priority:</strong>
                                                        <span
                                                            className={`editable-field badge ${priorityClasses[ticket.priority] || ''}`}
                                                            onClick={() => handleColumnChange(ticket, 'priority')}
                                                        >
                                                            {ticket.priority?.toUpperCase() || 'N/A'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="card-body">
                                        <div className="row">
                                            <div className="col-12 col-md-12 col-lg-8 mb-4">
                                                <div className="card p-4">
                                                    <div className="row">
                                                        <div className="col-12">
                                                            <div className="form-group d-flex justify-content-between mb-3">
                                                                {service === 'basic' ? (
                                                                    <>
                                                                        <label className="form-label">Product Name:</label>
                                                                        <span>{ticket.customerProduct?.product?.name || 'N/A'}</span>
                                                                    </>
                                                                ) : service === 'premium' ? (
                                                                    <>
                                                                        <label className="form-label">Plan Name:</label>
                                                                        <span>{ticket.subscription?.plan?.name || 'N/A'}</span>
                                                                    </>
                                                                ) : null}
                                                            </div>
                                                            <div className="form-group d-flex justify-content-between mb-3" >
                                                                <label className="form-label">Assigned To:</label>
                                                                <span className='text-capitalize badge bg-info editable-field' onClick={() => handleAssign(ticket.id)}>{ticket.assignedTo?.name || 'Assign Now'}</span>
                                                            </div>
                                                            <div className="form-group d-flex justify-content-between mb-3">
                                                                <label className="form-label">Description:</label>
                                                                <span>{ticket.description || 'N/A'}</span>
                                                            </div>
                                                            <div className="form-group d-flex justify-content-between mb-3">
                                                                <label className="form-label">Created By:</label>
                                                                <span>{ticket.createdBy?.name || 'N/A'}</span>
                                                            </div>
                                                            <div className="form-group d-flex justify-content-between mb-3">
                                                                <label className="form-label">Customer:</label>
                                                                {service === 'basic' || service === 'anonymous' ? (
                                                                    <span>{ticket?.customer?.company || 'N/A'}</span>) : (
                                                                    <span>{ticket?.subscription?.customer?.company || 'N/A'}</span>
                                                                )}
                                                            </div>
                                                            <div className="form-group d-flex justify-content-between mb-3">
                                                                <label className="form-label">Created At:</label>
                                                                <span>{DateTimeFormat(ticket.createdAt) || 'N/A'}</span>
                                                            </div>
                                                             {service === 'basic' && (
                                                            <form onSubmit={referenceForm.handleSubmit(onReferenceTicketSubmit)} className="w-100">
                                                                <label className="form-label">Reference Ticket No (OEM):</label>

                                                                <div className="relative-container mb-2">
                                                                    <input
                                                                        type='text'
                                                                        className='form-control'
                                                                        {...referenceForm.register('reference_ticket', { required: "Reference ticket is required" })}
                                                                        placeholder='Reference Ticket No (OEM)'
                                                                    />

                                                                    {referenceForm.formState.errors.reference_ticket && (
                                                                        <small className="text-danger error-text">
                                                                            {referenceForm.formState.errors.reference_ticket.message}
                                                                        </small>
                                                                    )}
                                                                </div>

                                                                <button type="submit" className='btn btn-primary btn-sm'>Save</button>
                                                            </form>
                                                             )}
                                                        </div>
                                                    </div>
                                                </div>
                                                {service === 'premium' && (
                                                    <div className="card p-4">
                                                        <div className="row">
                                                            <div className="col-12">
                                                                <h3 className='text-primary'>Subscription Details</h3>

                                                                <div className="form-group d-flex justify-content-between mb-3">
                                                                    <label className="form-label">Start Date:</label>
                                                                    <span>{DateFormat(ticket.subscription?.start_date) || 'N/A'}</span>
                                                                </div>
                                                                <div className="form-group d-flex justify-content-between mb-3">
                                                                    <label className="form-label">End Date</label>
                                                                    <span>{DateFormat(ticket.subscription?.end_date) || 'N/A'}</span>
                                                                </div>
                                                                <div className="form-group d-flex justify-content-between mb-3">
                                                                    <label className="form-label">Status</label>
                                                                    <span className={`badge mb-auto ${ticket?.subscription?.status === 'active' ? 'bg-success' : 'bg-danger'}`}>
                                                                        {ticket?.subscription?.status}
                                                                    </span>

                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                                <div className="card mt-4">

                                                    <div className="card-body">
                                                        <div className="mt-4">
                                                            {comments?.length ? (
                                                                comments.map(({ replier, reply, createdAt }, index) => {
                                                                    const { name } = replier || {};
                                                                    const guard = user?.gaurd || 'user';
                                                                    const companyName = user?.company_name || 'N/A';

                                                                    return (
                                                                        <div
                                                                            key={index}
                                                                            className="mb-3 p-2 border-bottom"
                                                                            style={{
                                                                                boxShadow: '0 2px 4px rgba(203, 204, 223, 0.1)',
                                                                                borderRadius: '8px',
                                                                            }}
                                                                        >
                                                                            <div className="d-flex justify-content-between align-items-center">
                                                                                <div>
                                                                                    <strong className="me-1 text-primary">{name}</strong>
                                                                                    <small className="text-muted"> ({`${guard} - ${companyName}`})</small>
                                                                                </div>
                                                                                <small className="text-muted">{new Date(createdAt).toLocaleString()}</small>
                                                                            </div>
                                                                            <p className="mt-2 mb-0 small">{reply}</p>
                                                                        </div>
                                                                    );
                                                                })
                                                            ) : (
                                                                <p>No replies yet.</p>
                                                            )}
                                                        </div>
                                                        <div className="form-group position-relative">
                                                            <form onSubmit={handleSubmit(onSubmit)}>
                                                                <textarea
                                                                    {...register('reply', { required: true, maxLength: 255 })}
                                                                    className="form-control"
                                                                    name='reply'
                                                                    data-autosize
                                                                    rows="1"
                                                                    placeholder="Add a reply"
                                                                    style={{ paddingRight: '60px', paddingBottom: '30px' }}
                                                                ></textarea>
                                                                {errors.reply && (
                                                                    <small className="text-danger">Reply is required</small>
                                                                )}

                                                                {/* Icons Positioned Inside the Textarea */}
                                                                <div className="position-absolute d-flex justify-content-end" style={{ bottom: '10px', right: '10px' }}>
                                                                    {/* Paperclip Icon */}
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-light btn-sm me-2"
                                                                        title="Attach File"
                                                                        onClick={() => console.log('File button clicked')}
                                                                    >
                                                                        <i className="fe fe-paperclip"></i>
                                                                    </button>

                                                                    {/* Reply Button */}
                                                                    <button type="submit" className="btn btn-primary btn-sm" title="Submit Reply">
                                                                        Reply
                                                                    </button>
                                                                </div>
                                                            </form>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="col-12 col-md-12 col-lg-4 mb-4">
                                                <RecentActivity type={type} />
                                            </div>
                                            <LogsTable allCallLogs={allCallLogs} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {showAssignModal && (
                <Assign
                    show={showAssignModal}
                    handleClose={handleClose}
                    handleSubmit={assignForm.handleSubmit}
                    onSubmit={onAssignSubmit}
                    AssigneeList={usersList}
                    control={assignForm.control}
                    errors={assignForm.formState.errors}
                />
            )}

            {showStatusPriorityModal && (
                <EditTicketModal
                    show={showStatusPriorityModal}
                    handleSubmit={statusPriorityForm.handleSubmit}
                    handleClose={handleClose}
                    onSubmit={onStatusPrioritySubmit}
                    id={ticketId}
                    control={statusPriorityForm.control}
                    errors={statusPriorityForm.formState.errors}
                    options={columnType === 'priority' ? priorityOptions : statusOptions}
                    type={columnType}
                />
            )}
            {showCallLogModal && (
                <CallLog
                    show={showCallLogModal}
                    handleClose={handleClose}
                    handleSubmit={callLogForm.handleSubmit}
                    onSubmit={onCallLogSubmit}
                    register={callLogForm.register}
                    errors={callLogForm.formState.errors}
                    selectedStatus={selectedStatus}
                    handleCallLog={handleCallLog}
                    statusData={statusData}
                    reset={reset}
                    today={today}
                />
            )}
            <ToastContainer containerId={'ticket-toast-container'} />
        </>
    );
}

export default ViewTicket;
